import {useRef} from 'react';
import {
    Alert,
    Button,
    Group,
    SimpleGrid,
} from '@mantine/core';
import {useNavigate} from 'react-router-dom';
import {I18n} from 'aws-amplify/utils';

import {
    DataTableTokenPagination,
    MantineDataTable,
    TableSearchForm,
} from '@flexinet/ui-components';

import {useZones} from '../hooks/useZones';
import {useZonesSearch} from '../hooks/useZonesSearch';
import {useZonesFilter} from '../hooks/useZonesQuery';
import {useZonesTableColumns} from '../hooks/useZonesTableColumns';
import {ConnectionFailuresAlert} from '../components/ConnectionFailuresAlert';
import {DnsZone} from '../types';
import config from '../config';

// ⚠️ NAMED ZonesTable, NOT RecordsTable.
//
// The hosted-zones module's `RecordsTable.tsx` renders ZONES, not records — a
// documented naming decoy that FNP-1194 exists to rename. This module has a
// sibling records concept (`/zones/:zoneId/records`), so reproducing that name
// here would make "RecordsTable" ambiguous between two real things in the same
// feature area rather than merely misleading in one.
export const ZonesTable = () => {

    const {columns} = useZonesTableColumns();
    const navigate = useNavigate();

    const {
        items,
        visibleItems,
        failures,
        sortStatus,
        setSortStatus,
        loading,
        isError,
        isFetchingNextPage,
        nextToken,
        fetchRecords,
        reload,
        isReloading,
    } = useZones();

    // Same module-scoped stores the list hook filters on (FNP-815) — this
    // component owns the search input, the hook owns the filtering.
    const {
        keyword,
        handleFilterByKeyword,
    } = useZonesSearch();

    const {filters} = useZonesFilter();

    const scrollViewportRef = useRef<HTMLDivElement>(null);

    // NO ROW SELECTION, deliberately. There is no delete and no batch operation
    // on the read half for a checkbox to apply to — the mutations do not exist
    // on the schema yet. Stated rather than left silent, because "no selection"
    // and "selection was forgotten" look identical in a diff.
    //
    // ─── RESERVED FOR THE CRUD FOLLOW-UP ───
    // Selection, a delete batch action and a row Edit action belong here, all
    // gated on useZoneWriteCapabilities (canDeleteZones / canWriteRecords).
    // The hiding decision is already built — see hooks/useZoneWriteCapabilities.

    // The empty states mean DIFFERENT things and `noRecordsText` is one string,
    // so the distinction is branched here rather than flattened into a single
    // message that sends an operator hunting for a bug that is not there.
    const searching = keyword.trim() !== '';
    const filtering = Object.keys(filters).length > 0;

    let noRecordsText = I18n.get('No zones found on your connected providers');
    if (searching && filtering) {
        noRecordsText = I18n.get('No loaded zone matches this search and filter');
    } else if (searching) {
        noRecordsText = I18n.get('No loaded zone matches this search');
    } else if (filtering) {
        noRecordsText = I18n.get('No loaded zone matches this filter');
    } else if (failures.length > 0) {
        // ⚠️ The empty table plus a failure is NOT "you have no zones". Every
        // connection that could be read returned nothing, and at least one
        // could not be read at all — so the honest answer is that this may not
        // be the whole picture.
        noRecordsText = I18n.get('No zones loaded. See the connection warning above — some connections could not be read.');
    }

    return (
        <SimpleGrid verticalSpacing={'xl'} m={'sm'}>
            <TableSearchForm handleFilterByKeyword={handleFilterByKeyword}/>

            {/* ⚠️ THE FAILURE BANNER SITS ABOVE THE TABLE AND THE TABLE STILL
                RENDERS. Partial success is success-with-a-warning: the zones
                that loaded are real and complete for their connections, and
                hiding them behind an error state would be a worse lie than the
                silent undercount this banner exists to prevent. */}
            <ConnectionFailuresAlert
                failures={failures}
                onRetry={reload}
                isRetrying={isReloading}
            />

            {/* Distinct from the banner above: this is the QUERY failing, so
                nothing at all is known — not a partial answer. */}
            {isError && (
                <Alert color={'red'} title={I18n.get('Could not load zones')}>
                    <Group justify={'space-between'} align={'center'}>
                        {I18n.get('The domains API did not return your zones. This is not a provider problem — the request itself failed.')}
                        <Button
                            variant={'outline'}
                            size={'xs'}
                            onClick={reload}
                            loading={isReloading}
                        >
                            {I18n.get('Retry')}
                        </Button>
                    </Group>
                </Alert>
            )}

            <MantineDataTable<DnsZone>
                columns={columns}
                records={visibleItems}
                idAccessor={'id'}
                // Client-side sorting, over the pages loaded so far — the same
                // scope the keyword and filters work on. Token pagination
                // cannot reorder rows the browser has not fetched.
                sortStatus={sortStatus}
                onSortStatusChange={setSortStatus}
                fetching={loading}
                noRecordsText={noRecordsText}
                onScrollToBottom={() => fetchRecords()}
                scrollViewportRef={scrollViewportRef}
                withTableBorder={true}
                // The connectionId rides along, because /zones/:zoneId alone
                // cannot address a zone — getDnsZone needs both.
                onRowClick={({record}) =>
                    navigate(`${config.basePath}/${encodeURIComponent(record.id)}?connectionId=${encodeURIComponent(record.connectionId)}`)}
            />

            <DataTableTokenPagination
                loadedCount={visibleItems.length}
                unfilteredCount={items.length}
                // Passing a term is what selects the filtered footer state —
                // omit it and a narrowed view silently reports the unfiltered
                // one. The provider/delegation filters narrow the same window
                // the keyword does, so either one must select that state.
                filterTerm={searching ? keyword : (filtering ? 'filtered' : undefined)}
                noun={'zone'}
                nextToken={nextToken}
                isFetchingMore={isFetchingNextPage}
                handleLoadMore={fetchRecords}
            />
        </SimpleGrid>
    );
};
